<template>
    <button
    class="button-floating"
    :disabled="disabled"
    :class="[{ 'extended': extended }, variant]"
    @click.stop="emit('clicked')">
        <!-- Button Icon -->
        <app-icon class="icon"/>
        <!-- Button label -->
        <div class="label">{{ label }}</div>
    </button>
</template>

<script setup>
/* Vue */
import { ref, computed, defineAsyncComponent } from 'vue'
/* Emmitters */
const emit = defineEmits(['clicked'])

/** Props received:
@label {String}
@icon {String}
@variant {String} can be [primary (default), secondary]
@extended {Boolean} to extend the button and show label
*/
const props = defineProps({
    label: String,
	icon: {
        type: String,
        default: 'filter'
    },
    variant: {
        type: String,
        default: 'primary'
    },
    extended: Boolean
})

/* Computed */
const appIcon = computed(() => {
    const icon = props.icon
	return defineAsyncComponent(() => import('@/assets/icons/' + icon + '.svg?inline'))
})

/* Boolean controller to show button label */
const showLabel = computed(() => {
    return props.extended
})
</script>

<style lang="scss" scoped>
.button-floating {
    display: flex;
    align-items: center;
    gap: 8px;

    width: max-content;
    max-width: 200px;
    height: 48px;

    padding: 18px 16px;
    border-radius: 24px;

    @include H7-Medium;
    box-shadow: var(--box-shadow-3);

    cursor: pointer;

    transition: max-width 0.5s ease-in-out;

    overflow: hidden;

    svg.icon {
        flex-shrink: 0;

        width: 16px;
        height: 16px;
    }

    .label {
        opacity: 1;

        transition: opacity 0.2s 0.2s ease-in-out;
    }

    &.primary {
        color: var(--secondary-light-4);
        background-color: var(--primary);

        svg.icon {
            :deep(.change-color-fill) {
                fill: var(--secondary-light-4);
            }
        }

        @media (hover: hover) and (pointer: fine) {
            &:hover {
                background-color: var(--button-primary-state-hover);
            }
        }

        &:active {
            background-color: var(--button-primary-state-press);
        }
    }

    &.secondary {
        color: var(--secondary-dark-2);
        background-color: var(--secondary-light-4);

        svg.icon {
            :deep(.change-color-fill) {
                fill: var(--secondary-dark-2);
            }
        }

        @media (hover: hover) and (pointer: fine) {
            &:hover {
                background-color: var(--button-secondary-state-hover);
            }
        }

        &:active {
            background-color: var(--button-secondary-state-press);
        }
    }

    &:not(.extended) {
        max-width: 48px;

        border-radius: 24px;

        .label {
            opacity: 0;
        }
    }
}
</style>
